import { useEffect, useState } from "react";

import { adbParentFs } from "../lib/selection";
import type { Selection } from "../lib/selection";
import type { AvailMode } from "../lib/viewMode";
import type { TopologyPayload } from "../lib/types/topology";
import type { ViewMode } from "../lib/viewMode";


interface ConfigToolbarProps {
  topology: TopologyPayload;
  selection?: Selection;
  onSelect: (s: Selection | undefined) => void;
  viewMode: ViewMode;
  onViewModeChange: (v: ViewMode) => void;
  /** Availability sub-mode: single-trip detail vs 30-day site rollup. */
  availMode?: AvailMode;
  onAvailModeChange?: (m: AvailMode) => void;
  /** True when the fleet has site rollup data (enables the Site tab). */
  hasSite?: boolean;
  /** Site (tail) + trip pickers — demo histories, navigable. */
  sites?: { tail: string; code?: string | null }[];
  siteTail?: string;
  onSiteChange?: (tail: string) => void;
  trips?: { id: string; date: string; avg_score: number }[];
  tripId?: string;
  onTripChange?: (id: string) => void;
  /** Opens the fleet-specific event catalog browser. */
  onOpenEvents?: () => void;
}


interface Group {
  label: string;
  buttons: { key: string; label: string; active: boolean; onClick: () => void }[];
}


// Back / forward through the interaction trail. Selection, view and
// sub-mode changes all push history entries (URL is the source of truth in
// FleetPage), so these are plain browser-history hops. The Navigation API
// (Chromium) provides real can-go states scoped to same-origin entries —
// back stays disabled at the app's entry point instead of exiting the site;
// other browsers fall back to always-enabled.
function HistoryNav() {
  const [can, setCan] = useState({ back: true, fwd: true });
  useEffect(() => {
    const nav = (window as unknown as {
      navigation?: {
        canGoBack: boolean;
        canGoForward: boolean;
        addEventListener: (t: string, f: () => void) => void;
        removeEventListener: (t: string, f: () => void) => void;
      };
    }).navigation;
    if (!nav) return;
    const update = () => setCan({ back: nav.canGoBack, fwd: nav.canGoForward });
    update();
    nav.addEventListener("currententrychange", update);
    return () => nav.removeEventListener("currententrychange", update);
  }, []);

  const btn = "px-2 py-1 transition disabled:cursor-not-allowed disabled:opacity-30 " +
    "bg-slate-700 text-slate-200 enabled:hover:bg-slate-600";
  return (
    <div className="flex overflow-hidden rounded border border-slate-700"
         title="Step back / forward through your clicks">
      <button
        onClick={() => window.history.back()}
        disabled={!can.back}
        aria-label="Back"
        className={btn}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"
             strokeLinejoin="round">
          <path d="M15 5 8 12l7 7" />
        </svg>
      </button>
      <button
        onClick={() => window.history.forward()}
        disabled={!can.fwd}
        aria-label="Forward"
        className={btn}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"
             strokeLinejoin="round">
          <path d="m9 5 7 7-7 7" />
        </svg>
      </button>
    </div>
  );
}


export default function ConfigToolbar({
  topology, selection, onSelect, viewMode, onViewModeChange,
  availMode = "trip", onAvailModeChange, hasSite = false,
  sites, siteTail, onSiteChange, trips, tripId, onTripChange, onOpenEvents,
}: ConfigToolbarProps) {
  const isEx3 = topology.layout.topology.type === "nc-port";
  const seats = Object.values(topology.seats);
  const lrus = topology.lrus ?? [];

  const selSeat = selection?.kind === "seat"
    ? topology.seats[selection.seatId]
    : undefined;

  // ---- group builders -----------------------------------------------------

  // displayOffset: eX1 MCU values are 0-based internally but the hardware
  // designations are 1-based ("MCU 001") — display +1 so every surface
  // (LOPA boxes, zone cards, this bar) shows the same numbers.
  const ncGroup = (label: string, nums: number[], displayOffset = 0): Group => ({
    label,
    buttons: nums.map((n) => ({
      key: `nc-${n}`,
      label: String(n + displayOffset),
      active:
        (selection?.kind === "nc" && selection.id === n) ||
        (selection?.kind === "port" && selection.nc === n) ||
        (selSeat !== undefined && (isEx3 ? selSeat.nc : selSeat.mcu) === n),
      onClick: () => onSelect({ kind: "nc", id: n }),
    })),
  });

  // The FS implied by the current selection — used so the FS group lights
  // up when a downstream seat / ADB / port is what's actually selected.
  const impliedFs = (): number | undefined => {
    if (selection?.kind === "fs") return selection.idx + 1;
    if (selection?.kind === "adb") return adbParentFs(topology, selection.num) ?? undefined;
    if (selection?.kind === "dport") {
      return chainKind === "ADB"
        ? adbParentFs(topology, selection.num) ?? undefined
        : selection.num;
    }
    if (selSeat?.chain_kind === "ADB" && selSeat.chain_num !== undefined) {
      return adbParentFs(topology, selSeat.chain_num) ?? undefined;
    }
    if ((selSeat?.chain_kind === "FS" || selSeat?.chain_kind === "IS")) {
      return selSeat.chain_num;
    }
    return undefined;
  };

  // Chain parents parsed from the corrected mapping (FS / ADB / IS).
  const chainKind = seats.find((s) => s.chain_kind)?.chain_kind;
  const chainNums = chainKind
    ? [...new Set(
        seats.filter((s) => s.chain_kind === chainKind)
             .map((s) => s.chain_num)
             .filter((n): n is number => n !== undefined),
      )].sort((a, b) => a - b)
    : [];

  const chainParentGroup = (): Group | null => {
    if (!chainKind || chainNums.length === 0) return null;
    const isAdb = chainKind === "ADB";
    return {
      label: chainKind,
      buttons: chainNums.map((n) => ({
        key: `${chainKind}-${n}`,
        label: String(n),
        active:
          (isAdb && selection?.kind === "adb" && selection.num === n) ||
          (!isAdb && selection?.kind === "fs" && selection.idx + 1 === n) ||
          (selection?.kind === "dport" && selection.num === n) ||
          (selSeat?.chain_kind === chainKind && selSeat.chain_num === n),
        onClick: () => onSelect(
          isAdb ? { kind: "adb", num: n } : { kind: "fs", idx: n - 1 },
        ),
      })),
    };
  };

  // On ADB fleets the FS tier sits above the ADBs — offer it too.
  const fsAboveAdbGroup = (): Group | null => {
    if (chainKind !== "ADB") return null;
    const nums = lrus
      .filter((l) => l.category === "HeadEnd")
      .map((l) => l.designation.match(/^FS 0*(\d+)$/)?.[1])
      .filter((m): m is string => Boolean(m))
      .map(Number)
      .sort((a, b) => a - b);
    if (nums.length === 0) return null;
    const implied = impliedFs();
    return {
      label: "FS",
      buttons: nums.map((n) => ({
        key: `fs-${n}`,
        label: String(n),
        active: implied === n,
        onClick: () => onSelect({ kind: "fs", idx: n - 1 }),
      })),
    };
  };

  const aiGroup = (): Group | null => {
    const nums = lrus
      .filter((l) => l.category === "HeadEnd")
      .map((l) => l.designation.match(/^AI 0*(\d+)$/)?.[1])
      .filter((m): m is string => Boolean(m))
      .map(Number)
      .sort((a, b) => a - b);
    if (nums.length === 0) return null;
    return {
      label: "AI",
      buttons: nums.map((n) => ({
        key: `ai-${n}`,
        label: String(n),
        active:
          (selection?.kind === "ai" && selection.idx + 1 === n) ||
          (selection?.kind === "aiport" && selection.unit === "AI" &&
           selection.num === n),
        onClick: () => onSelect({ kind: "ai", idx: n - 1 }),
      })),
    };
  };

  // Contextual port group: ports of the currently selected chain parent.
  const portGroup = (): Group | null => {
    if (!chainKind) return null;
    let parentNum: number | undefined;
    if (selection?.kind === "fs" && chainKind !== "ADB") parentNum = selection.idx + 1;
    else if (selection?.kind === "adb")   parentNum = selection.num;
    else if (selection?.kind === "dport") parentNum = selection.num;
    else if (selSeat?.chain_kind === chainKind) parentNum = selSeat.chain_num;
    if (parentNum === undefined) return null;
    const ports = [...new Set(
      seats.filter((s) => s.chain_kind === chainKind && s.chain_num === parentNum)
           .map((s) => s.chain_port)
           .filter((p): p is number => p !== undefined),
    )].sort((a, b) => a - b);
    if (ports.length === 0) return null;
    const num = parentNum;
    return {
      label: `${chainKind} ${num} ports`,
      buttons: ports.map((p) => ({
        key: `dport-${num}-${p}`,
        label: `P${p}`,
        active:
          (selection?.kind === "dport" && selection.num === num && selection.port === p) ||
          (selSeat?.chain_num === num && selSeat?.chain_port === p),
        onClick: () => onSelect({ kind: "dport", num, port: p }),
      })),
    };
  };

  const mcuGroup = (): Group => {
    const nums = [...new Set(
      seats.map((s) => s.mcu)
           .filter((m): m is number => m !== undefined && m !== null),
    )].sort((a, b) => a - b);
    return ncGroup("MCU", nums, 1);  // display 1-based, matching "MCU 001" designations
  };

  const csGroup = (): Group => ({
    label: "CS",
    buttons: [1, 2].map((id) => ({
      key: `cs-${id}`,
      label: String(id),
      active: selection?.kind === "cs" && selection.id === id,
      onClick: () => onSelect({ kind: "cs", id }),
    })),
  });

  // Contextual power/NC port group: ports of the currently selected NC/MCU —
  // one-click chain switching without scrolling back to the header.
  const powerPortGroup = (labelPrefix: string, displayOffset = 0): Group | null => {
    let n: number | undefined;
    if (selection?.kind === "nc") n = selection.id;
    else if (selection?.kind === "port") n = selection.nc;
    else if (selSeat) n = isEx3 ? selSeat.nc : selSeat.mcu;
    if (n === undefined || n === null) return null;
    const nn = n;
    const ports = [...new Set(
      seats.filter((s) => (isEx3 ? s.nc : s.mcu) === nn).map((s) => s.port),
    )].sort((a, b) => a - b);
    if (ports.length === 0) return null;
    return {
      label: `${labelPrefix} ${nn + displayOffset} ports`,
      buttons: ports.map((p) => ({
        key: `pport-${nn}-${p}`,
        label: `P${p}`,
        active:
          (selection?.kind === "port" && selection.nc === nn && selection.port === p) ||
          ((isEx3 ? selSeat?.nc : selSeat?.mcu) === nn && selSeat?.port === p),
        onClick: () => onSelect({ kind: "port", nc: nn, port: p }),
      })),
    };
  };

  // ---- pick groups for the active view ------------------------------------

  const groups: Group[] = [];
  if (isEx3) {
    groups.push(csGroup());
    groups.push(ncGroup("NC", [1, 2]));
    const pg = powerPortGroup("NC");
    if (pg) groups.push(pg);
  } else if (viewMode === "mcu") {
    groups.push(mcuGroup());
    const ai = aiGroup();
    if (ai) groups.push(ai);
    const pg = powerPortGroup("MCU", 1);
    if (pg) groups.push(pg);
  } else {
    const fsTop = fsAboveAdbGroup();
    if (fsTop) groups.push(fsTop);
    const cp = chainParentGroup();
    if (cp) groups.push(cp);
    else groups.push(mcuGroup());  // no chain data → fall back to MCU
    const ai = aiGroup();
    if (ai) groups.push(ai);
    const pg = portGroup();
    if (pg) groups.push(pg);
  }

  return (
    <div className="flex items-center gap-4 overflow-x-auto border-t border-slate-700 bg-slate-800 px-4 py-2 text-sm text-slate-100 md:px-6 [&::-webkit-scrollbar]:hidden"
         style={{ scrollbarWidth: "none" }}>
      {groups.map((g) => (
        <div key={g.label} className="flex shrink-0 items-center gap-2">
          <span className="text-xs uppercase tracking-wide text-slate-400">
            {g.label}
          </span>
          <div className="flex gap-1">
            {g.buttons.map((b) => (
              <button
                key={b.key}
                onClick={b.onClick}
                className={
                  "rounded px-3 py-1 text-xs font-semibold transition " +
                  (b.active
                    ? "bg-sky-500 text-white"
                    : "bg-slate-700 text-slate-200 hover:bg-slate-600")
                }
              >
                {b.label}
              </button>
            ))}
          </div>
        </div>
      ))}
      <button
        onClick={() => onSelect(undefined)}
        disabled={!selection}
        className="shrink-0 rounded bg-slate-700 px-3 py-1 text-xs text-slate-200 hover:bg-slate-600 disabled:cursor-not-allowed disabled:opacity-40"
      >
        Reset
      </button>

      <div className="ml-auto flex shrink-0 items-center gap-2">
        <HistoryNav />
        <span className="mx-1 h-5 w-px bg-slate-600/70" aria-hidden />
        {onOpenEvents && (
          <button
            onClick={onOpenEvents}
            title="Browse every fault event applicable to this fleet"
            className="flex items-center gap-1.5 rounded border border-slate-600 bg-slate-700/60 px-2.5 py-1 text-xs text-slate-300 transition hover:bg-slate-600 hover:text-white"
          >
            {/* Open book — a reference catalog, not an active alert. */}
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2">
              <path d="M12 6c-1.5-1.6-3.6-2.2-6.2-2.2-1.4 0-2.7.2-3.8.7v15c1.1-.5 2.4-.7 3.8-.7 2.6 0 4.7.6 6.2 2.2 1.5-1.6 3.6-2.2 6.2-2.2 1.4 0 2.7.2 3.8.7v-15c-1.1-.5-2.4-.7-3.8-.7C15.6 3.8 13.5 4.4 12 6Z" />
              <path d="M12 6v15" />
            </svg>
            Event Catalog
          </button>
        )}

        <span className="mx-1 h-5 w-px bg-slate-600/70" aria-hidden />

        {/* Static configuration views — the aircraft's wiring, no trip data. */}
        <span className="text-xs text-slate-400">Config:</span>
        <div className="flex overflow-hidden rounded border border-slate-700">
          <button
            onClick={() => onViewModeChange("data")}
            title="Data chain topology (FS → ADB → SEB)"
            className={
              "px-3 py-1 text-xs font-semibold transition " +
              (viewMode === "data"
                ? "bg-slate-100 text-slate-900"
                : "bg-slate-700 text-slate-200 hover:bg-slate-600")
            }
          >
            Data
          </button>
          <button
            onClick={() => onViewModeChange("mcu")}
            disabled={isEx3}
            title={isEx3 ? "eX3 fleets have no MCUs" : "Power topology (MCU → ports)"}
            className={
              "px-3 py-1 text-xs font-semibold transition " +
              (viewMode === "mcu"
                ? "bg-slate-100 text-slate-900"
                : "bg-slate-700 text-slate-200 hover:bg-slate-600") +
              " disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-slate-700"
            }
          >
            MCU
          </button>
        </div>

        <span className="mx-1 h-5 w-px bg-slate-600/70" aria-hidden />

        {/* Dynamic view — per-trip / per-site scores, not wiring. */}
        <button
          onClick={() => onViewModeChange("avail")}
          title="Per-seat availability (trip detail or 30-day site rollup)"
          className={
            "flex items-center gap-1.5 rounded border px-3 py-1 text-xs font-semibold transition " +
            (viewMode === "avail"
              ? "border-emerald-400 bg-emerald-400 text-slate-900"
              : "border-emerald-500/50 bg-slate-700/40 text-emerald-300 hover:bg-slate-600")
          }
        >
          <span
            className={
              "h-1.5 w-1.5 rounded-full " +
              (viewMode === "avail" ? "bg-slate-900" : "bg-emerald-400")
            }
          />
          Availability
        </button>
        {/* Trip ↔ Site sub-toggle. Always rendered so the toolbar never
            reflows when views change — enabled only inside Availability. */}
        {onAvailModeChange && (
          <div
            className={
              "flex overflow-hidden rounded border border-slate-700 " +
              (viewMode !== "avail" ? "opacity-40" : "")
            }
            title={viewMode !== "avail"
              ? "Switch to Availability to pick Trip vs Site"
              : undefined}
          >
            <button
              onClick={() => onAvailModeChange("trip")}
              disabled={viewMode !== "avail"}
              className={
                "px-2.5 py-1 text-xs font-semibold transition disabled:cursor-not-allowed " +
                (availMode === "trip" && viewMode === "avail"
                  ? "bg-slate-100 text-slate-900"
                  : "bg-slate-700 text-slate-200 enabled:hover:bg-slate-600")
              }
            >
              Trip
            </button>
            <button
              onClick={() => onAvailModeChange("site")}
              disabled={viewMode !== "avail" || !hasSite}
              title={!hasSite ? "No site rollup data for this fleet" : undefined}
              className={
                "px-2.5 py-1 text-xs font-semibold transition disabled:cursor-not-allowed " +
                (availMode === "site" && viewMode === "avail"
                  ? "bg-slate-100 text-slate-900"
                  : "bg-slate-700 text-slate-200 enabled:hover:bg-slate-600")
              }
            >
              Site
            </button>
          </div>
        )}
        {/* Site (tail) + trip pickers. Always rendered at fixed widths so
            the toolbar never reflows — enabled only inside Availability. */}
        {sites && sites.length > 0 && onSiteChange && (
          <select
            value={siteTail ?? sites[0].tail}
            onChange={(e) => onSiteChange(e.target.value)}
            disabled={viewMode !== "avail"}
            title={viewMode !== "avail"
              ? "Site histories show in the Availability view"
              : "Switch aircraft (site)"}
            className="w-28 cursor-pointer rounded border-0 bg-white px-2 py-1 text-[12px] text-slate-800 focus:outline focus:outline-2 focus:outline-sky-500 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500 disabled:opacity-60"
          >
            {sites.map((s) => (
              <option key={s.tail} value={s.tail}>{s.tail}</option>
            ))}
          </select>
        )}
        {trips && trips.length > 0 && onTripChange && (
          <select
            value={tripId ?? trips[trips.length - 1].id}
            onChange={(e) => onTripChange(e.target.value)}
            disabled={viewMode !== "avail"}
            title={viewMode !== "avail"
              ? "Trip scores show in the Availability view"
              : "Jump to a trip in the 30-day window"}
            className="w-52 cursor-pointer rounded border-0 bg-white px-2 py-1 text-[12px] text-slate-800 focus:outline focus:outline-2 focus:outline-sky-500 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500 disabled:opacity-60"
          >
            {trips.map((t) => (
              <option key={t.id} value={t.id}>
                {t.id} · {t.avg_score.toFixed(1)}%
              </option>
            ))}
          </select>
        )}
      </div>
    </div>
  );
}
